import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { getContrastColor } from "@/modules/common/utils";
import { UUID } from "crypto";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from "react-hook-form";
import { useDebounce } from "use-debounce";
import { Options } from "@/modules/common/types";
import { SxProps, useTheme } from "@mui/material";

interface SingleSelectSearchProps<T extends FieldValues> {
  options: Options[];
  isLoading: boolean;
  label: string;
  defaultValue?: string | null;
  value?: string | null;
  onValueChange?: (value: string | null) => void;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  autoFocus?: boolean;
  size?: "small" | "medium";
  name?: Path<T>;
  control?: Control<T>;
  error?: string;
  disabled?: boolean;
  noOptionsText?: string;
  sx?: SxProps;
}

export default function SingleSelectSearch<T extends FieldValues>({
  options,
  isLoading,
  label,
  defaultValue,
  onValueChange,
  searchValue,
  onSearchValueChange,
  autoFocus,
  size = "small",
  name,
  control,
  error,
  disabled = false,
  noOptionsText = "No options found",
  sx,
}: SingleSelectSearchProps<T>) {
  const theme = useTheme();
  const formContext = useFormContext<T>();

  const findOptionByValue = (
    value: string | number | UUID | undefined | null
  ) => options.find((option) => option.value === value) || null;

  const [selectedOption, setSelectedOption] = React.useState<Options | null>(
    findOptionByValue(defaultValue)
  );

  const [searchText, setSearchText] = React.useState(searchValue || "");
  const [debouncedSearchText] = useDebounce(searchText, 500);

  React.useEffect(() => {
    onSearchValueChange?.(debouncedSearchText);
  }, [debouncedSearchText, onSearchValueChange]);

  React.useEffect(() => {
    setSearchText(searchValue || "");
  }, [searchValue]);

  React.useEffect(() => {
    setSelectedOption(findOptionByValue(defaultValue));
  }, [defaultValue]);

  const handleChange = (newValue: Options | null) => {
    setSelectedOption(newValue);
    onValueChange?.(newValue?.value?.toString() ?? null);
  };

  // Extract TextField-specific styles from sx prop
  const { borderRadius, height, ...autocompleteStyles } = (sx as any) || {};

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      ...(borderRadius && { borderRadius: borderRadius }),
      ...(height && { height: height }),
    },
  };

  const renderAutocomplete = (field: any) => (
    <Autocomplete
      value={selectedOption}
      onChange={(_, newValue) => {
        handleChange(newValue);
        field.onChange(newValue ? newValue.value : null);
      }}
      disabled={disabled}
      inputValue={searchText}
      autoFocus={autoFocus}
      noOptionsText={noOptionsText}
      onInputChange={(_, newInputValue) => {
        setSearchText(newInputValue);
      }}
      sx={{ width: "100%", ...autocompleteStyles }}
      renderOption={(props, option) => {
        const hasPhoneNumber = !!option.phoneNumber;
        return (
          <li
            {...props}
            key={option.value}
            style={{
              backgroundColor:
                option.oldColor || option.new_color || option.color,
              color: getContrastColor(
                option.oldColor || option.new_color || option.color
              ),
            }}
          >
            {hasPhoneNumber ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Box component="span">{option.label}</Box>
                <Box
                  component="span"
                  sx={{
                    ...theme.nawy.typography.english.variants.body.xs.regular,
                    opacity: 0.7,
                    marginTop: "2px",
                  }}
                >
                  {option.phoneNumber}
                </Box>
              </Box>
            ) : (
              <span>{option.label}</span>
            )}
          </li>
        );
      }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      id="asynchronous-demo"
      getOptionLabel={(option) => option?.label || ""}
      options={options}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error}
          size={size}
          sx={textFieldSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: { width: "100%", ...sx },
          }}
        />
      )}
    />
  );

  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) =>
          renderAutocomplete({
            ...field,
            value: findOptionByValue(field.value),
          })
        }
      />
    );
  } else if (formContext && name) {
    try {
      const { field } = useController({ name, control: formContext.control });
      return renderAutocomplete({
        ...field,
        value: findOptionByValue(field.value),
      });
    } catch (error) {
      console.error("Error in SingleSelectSearch:", error);
      return renderAutocomplete({ value: null, onChange: () => {} });
    }
  } else {
    console.warn(
      "SingleSelectSearch: No form context found. Rendering uncontrolled component."
    );
    return renderAutocomplete({ value: null, onChange: () => {} });
  }
}
